import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useParams } from 'react-router-dom'
import { SupportedChainId } from '../../constants/networks'
import { capitalizeFirstLetter, formatTokenAmount } from '../../utils/math-utils'
import useMonitors from '../../hooks/useMonitors'
import useThresholds from '../../hooks/useThresholds'
import Network from '../general/Network'
import { shortenAddress } from '../../utils/web3-utils'
import toast from 'react-hot-toast'

export default function AllMonitors() {
  const params = useParams()
  const data = useMonitors(params.id)
  const thresholds = useThresholds(params.id)
  const [filters, setFilters] = useState({ address: '', symbol: '', chainId: '' })
  const [showAboveThreshold, setShowAboveThreshold] = useState(false)

  const SUPPORTED_CHAINS = Object.entries(SupportedChainId).map(([key, value]) => ({
    label: capitalizeFirstLetter(key.toLowerCase()) as string,
    value,
  }))

  const transformBalances = (balances: any) => {
    const result: any[] = []
    for (const chainId in balances) {
      const tokens = balances[chainId]
      for (const address in tokens) {
        result.push({ ...tokens[address], address, chainId })
      }
    }
    return result
  }

  const transformedData = transformBalances(data?.data)

  const handleFilterChange = (key: string, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }))

  const filteredData = transformedData
    ?.filter((token) => {
      const usd = (formatTokenAmount(token.totalAmount, token.decimals, { digit: 7 }) * token.price).toFixed(2)
      const threshold = thresholds.data?.[token.chainId]
      const isAboveThreshold = usd > (threshold || 0)
      return (
        (filters.address === '' || token.address.toLowerCase().includes(filters.address.toLowerCase())) &&
        (filters.symbol === '' || (token.symbol || '').toLowerCase().includes(filters.symbol.toLowerCase())) &&
        (filters.chainId === '' || token.chainId === filters.chainId) &&
        (!showAboveThreshold || isAboveThreshold)
      )
    })
    ?.sort((a, b) => {
      const amtA = formatTokenAmount(a.totalAmount, a.decimals, { digit: 7 }) * a.price
      const amtB = formatTokenAmount(b.totalAmount, b.decimals, { digit: 7 }) * b.price
      return amtB - amtA
    })

  return (
    <Wrap>
      <FilterBar>
        <FilterInput
          type="text"
          placeholder="Filter by address"
          value={filters.address}
          onChange={(e) => handleFilterChange('address', e.target.value)}
        />
        <FilterInput
          type="text"
          placeholder="Filter by symbol"
          value={filters.symbol}
          onChange={(e) => handleFilterChange('symbol', e.target.value)}
        />
        <FilterSelect
          value={filters.chainId}
          onChange={(e) => handleFilterChange('chainId', e.target.value)}
        >
          <option value="">All chains</option>
          {SUPPORTED_CHAINS.map((chain) => (
            <option key={chain.value} value={chain.value}>
              {chain.label}
            </option>
          ))}
        </FilterSelect>

        <ToggleBtn active={showAboveThreshold} onClick={() => setShowAboveThreshold((v) => !v)}>
          Above threshold
          <TogglePip active={showAboveThreshold} />
        </ToggleBtn>

        {filteredData && (
          <Count>{filteredData.length} token{filteredData.length !== 1 ? 's' : ''}</Count>
        )}
      </FilterBar>

      {!data?.data ? (
        <LoaderBar />
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>Network</Th>
                <Th>Symbol</Th>
                <Th>Address</Th>
                <Th>Threshold</Th>
                <Th>Amount</Th>
                <Th>Price</Th>
                <Th>USD</Th>
              </tr>
            </thead>
            <tbody>
              {filteredData?.map((token, index) => {
                const usd = (formatTokenAmount(token.totalAmount, token.decimals, { digit: 7 }) * token.price).toFixed(2)
                const threshold = thresholds.data?.[token.chainId]
                const isAboveThreshold = threshold ? Number(usd) > Number(threshold) : null

                return (
                  <tr key={index}>
                    <Td><Network network={token.chainId} /></Td>
                    <Td><Symbol>{token.symbol}</Symbol></Td>
                    <Td>
                      <AddressCell>
                        <Mono>{shortenAddress(token.address)}</Mono>
                        <CopyBtn onClick={() => { navigator.clipboard.writeText(token.address); toast.success('Copied!') }} title="Copy address">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        </CopyBtn>
                      </AddressCell>
                    </Td>
                    <Td>
                      {threshold != null
                        ? <Mono>{threshold < 1e-8 ? threshold.toFixed(11) : threshold}</Mono>
                        : <Muted>—</Muted>}
                    </Td>
                    <Td><Mono>{formatTokenAmount(token.totalAmount, token.decimals, { digit: 2 })}</Mono></Td>
                    <Td><Mono>{token.price.toFixed(4)}</Mono></Td>
                    <Td>
                      <UsdValue status={isAboveThreshold}>
                        ${usd}
                        {isAboveThreshold === true && <StatusDot ok />}
                        {isAboveThreshold === false && <StatusDot ok={false} />}
                      </UsdValue>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Wrap>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const FilterInput = styled.input`
  background: #111;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 6px;
  color: #ededed;
  font-size: 12px;
  padding: 0 10px;
  height: 32px;
  outline: none;
  width: 180px;
  transition: border-color 0.15s;

  &::placeholder { color: #777; }
  &:focus { border-color: rgba(111,92,230,0.5); }
`

const FilterSelect = styled.select`
  background: #111;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 6px;
  color: #a0a0a0;
  font-size: 12px;
  padding: 0 10px;
  height: 32px;
  outline: none;
  cursor: pointer;
  transition: border-color 0.15s;

  &:focus { border-color: rgba(111,92,230,0.5); }
  option { background: #161616; color: #ededed; }
`

const ToggleBtn = styled.button<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  background: ${({ active }) => active ? 'rgba(111,92,230,0.15)' : 'rgba(255,255,255,0.04)'};
  border: 1px solid ${({ active }) => active ? 'rgba(111,92,230,0.4)' : 'rgba(255,255,255,0.08)'};
  border-radius: 6px;
  color: ${({ active }) => active ? '#a89aff' : '#555'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover { border-color: rgba(255,255,255,0.15); color: #a0a0a0; }
`

const TogglePip = styled.span<{ active: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ active }) => active ? '#33C2B0' : '#3a3a3a'};
  flex-shrink: 0;
  transition: background 0.2s;
`

const Count = styled.span`
  color: #777;
  font-size: 12px;
  margin-left: auto;
`

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const LoaderBar = styled.div`
  height: 2px;
  background: rgba(255,255,255,0.06);
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(111,92,230,0.5), transparent);
    animation: ${shimmer} 1.4s ease infinite;
  }
`

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  color: #a1a1a1;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`

const Td = styled.td`
  padding: 10px 12px;
  color: #a0a0a0;
  font-size: 13px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(255,255,255,0.04);

  tr:last-child & { border-bottom: none; }
`

const Symbol = styled.span`
  color: #ededed;
  font-weight: 500;
`

const Mono = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: #a0a0a0;
`

const Muted = styled.span`
  color: #777;
`

const UsdValue = styled.span<{ status: boolean | null }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${({ status }) =>
    status === true ? '#33C2B0' : status === false ? '#ff4444' : '#a0a0a0'};
`

const AddressCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const CopyBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #555;
  display: flex;
  transition: color 0.15s;
  &:hover { color: #a0a0a0; }
`

const StatusDot = styled.span<{ ok: boolean }>`
  display: inline-block;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ ok }) => ok ? '#33C2B0' : '#ff4444'};
  flex-shrink: 0;
`
